import React, { createContext, useState, useEffect } from 'react';
import api from '../services/api';
import { identify } from '../services/monitoring';

export const AuthContext = createContext();

// Where the ADMIN'S OWN session is parked while they are signed in as a seller.
// Separate keys from 'token'/'user' so the app below never has to know it is
// looking at a borrowed session — every screen reads the normal keys and sees
// the seller, which is the entire point of impersonating.
const ADMIN_TOKEN_KEY = 'adminToken';
const ADMIN_USER_KEY = 'adminUser';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  // Read straight from storage rather than kept in state alone, so a page
  // refresh mid-impersonation still shows the banner instead of quietly
  // leaving an admin driving somebody else's account with no way back.
  const [adminSession, setAdminSession] = useState(() => {
    try {
      const raw = localStorage.getItem(ADMIN_USER_KEY);
      return raw && localStorage.getItem(ADMIN_TOKEN_KEY) ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  // Which account an error belongs to. The id only — never the email, because
  // an error tracker is not somewhere to accumulate a list of who uses the
  // platform. Cleared on sign-out so the next person's errors are not filed
  // against the last one.
  useEffect(() => { identify(user); }, [user]);

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      setToken(storedToken);
      const cached = JSON.parse(storedUser);
      setUser(cached);

      // Reconcile the cached user with the server.
      //
      // localStorage holds whatever the login response contained on the day
      // the session started, so any field added to that payload afterwards is
      // missing for everyone already signed in — and stays missing until they
      // happen to log out. `isPrimeSeller` was exactly that: a prime seller
      // saw none of the prime-seller tools, with nothing on screen explaining
      // why. Refreshing here means the session self-heals on the next page
      // load instead.
      //
      // Deliberately not awaited and deliberately silent: a failure here (no
      // network, expired token) must leave the cached session working rather
      // than sign anyone out. An expired token is handled by the API layer's
      // own 401 path, as it always was.
      api.get('/auth/seller-profile')
        .then(({ data }) => {
          if (!data?.account) return;
          const merged = { ...cached, ...data.account };
          localStorage.setItem('user', JSON.stringify(merged));
          setUser(merged);
        })
        .catch(() => {});
    }
    setLoading(false);
  }, []);

  // Shared by login/register and by flows that get a session back from a
  // different endpoint (e.g. vendor registration) — keeps the localStorage
  // keys and state updates in exactly one place.
  const applySession = (nextUser, nextToken) => {
    localStorage.setItem('token', nextToken);
    localStorage.setItem('user', JSON.stringify(nextUser));
    setUser(nextUser);
    setToken(nextToken);
  };

  // One object rather than five positional arguments: `register(a, b, c, d, e)`
  // is a call nobody can read at the call site, and adding `fullName` in the
  // middle of the old signature would have silently shifted every argument
  // after it.
  // REGISTERING NO LONGER SIGNS ANYBODY IN, because it no longer creates an
  // account. What comes back is `{ pending: true, email, ticket }` — the
  // signup itself, sealed, which the browser carries for the next ten minutes
  // BECAUSE THE SERVER KEEPS NOTHING. `completeSignup` below is what actually
  // creates the account and starts the session.
  //
  // This is what stops an abandoned attempt from owning the email address:
  // nothing is stored anywhere, so signing up again with it just works.
  const register = async ({ email, password, sellerName, fullName, phoneNumber }) => {
    const response = await api.post('/auth/register', {
      email,
      password,
      sellerName,
      fullName,
      // Both required by the server now. Trimmed here so a trailing space
      // typed on a phone keyboard is not what fails the request.
      phoneNumber: String(phoneNumber || '').trim()
    });
    return response;
  };

  // The code was right: an account now exists and this is its first session.
  const completeSignup = async ({ ticket, code }) => {
    const response = await api.post('/auth/signup/verify', { ticket, code });
    applySession(response.data.user, response.data.token);
    return response;
  };

  // `identifier` is an email OR a mobile number — the server works out which.
  // Sent under its own name rather than as `email`, so a mobile is never
  // silently validated as an address by anything in between.
  const login = async (identifier, password) => {
    try {
      const response = await api.post('/auth/login', {
        identifier,
        password
      });

      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      setUser(response.data.user);
      setToken(response.data.token);

      return response;
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    // Clears the parked admin session too. Signing out has to mean no session
    // is left behind — a stale adminToken would otherwise sit in the browser
    // of whoever used it next.
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(ADMIN_USER_KEY);
    setAdminSession(null);
    setUser(null);
    setToken(null);
  };

  // Step into a seller's account. The admin's own session is parked first, so
  // returning is always possible even if the impersonated session expires.
  //
  // Reloads rather than re-rendering: credits, compliance, unread support and
  // every open list were all fetched for whoever was signed in a moment ago.
  // Swapping the token in place would leave an admin looking at their own
  // numbers on a seller's account — a full load is slower and correct.
  const startImpersonation = (nextUser, nextToken) => {
    localStorage.setItem(ADMIN_TOKEN_KEY, localStorage.getItem('token') || '');
    localStorage.setItem(ADMIN_USER_KEY, localStorage.getItem('user') || '{}');
    applySession(nextUser, nextToken);
    window.location.assign('/dashboard');
  };

  // Back to the admin's own account. Restores the parked session; if it is
  // somehow gone, falls back to signing out rather than stranding the browser
  // in a session it cannot name.
  const stopImpersonation = () => {
    const savedToken = localStorage.getItem(ADMIN_TOKEN_KEY);
    const savedUser = localStorage.getItem(ADMIN_USER_KEY);
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(ADMIN_USER_KEY);

    if (!savedToken || !savedUser) {
      logout();
      window.location.assign('/login');
      return;
    }
    localStorage.setItem('token', savedToken);
    localStorage.setItem('user', savedUser);
    setAdminSession(null);
    window.location.assign('/admin/users');
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      register,
      completeSignup,
      applySession,
      logout,
      startImpersonation,
      stopImpersonation,
      // The admin behind a borrowed session, or null in a normal one.
      impersonatedBy: adminSession,
      isImpersonating: !!adminSession,
      isAuthenticated: !!token
    }}>
      {children}
    </AuthContext.Provider>
  );
};
